import { useQuery } from '@tanstack/react-query';
import { Copy, Download, Pencil, Trash2, TriangleAlert, Upload } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Link } from 'react-router';
import { StatusBadge } from '../../components/ui/Badge.js';
import { Button } from '../../components/ui/Button.js';
import { type Column, DataTable } from '../../components/ui/DataTable.js';
import { Input, Select } from '../../components/ui/FormField.js';
import { PageHeader } from '../../components/ui/PageHeader.js';
import { RowActions } from '../../components/ui/RowActions.js';
import { UrlFilterChip } from '../../components/ui/UrlFilterChip.js';
import { useAbility } from '../../context/AbilityContext.js';
import { useConfirmedMutation } from '../../hooks/useConfirmedMutation.js';
import { useUrlFilter } from '../../hooks/useUrlFilter.js';
import { api } from '../../services/api.js';
import { RESOURCE_KEYS } from '../../services/resourceKeys.js';
import { PUBLISH_PATCH, UNPUBLISH_PATCH, canPublish, isPublished } from '../../utils/carPublish.js';
import { CarHostsChips } from './CarHostsChips.js';

interface Car {
  _id: string;
  /** 377: the car's hosts with names (staff reads). */
  hosts?: Array<{ id: string; name: string }>;
  name: string;
  plate_number: string;
  listing_status: string;
  operational_status: string;
  year: number;
  transmission: string;
  fuel_type: string;
  seat_capacity: number;
  // 050: the display rate `/cars` publishes, resolved from the car's rate-card
  // hierarchy. `null` means no usable rate card — the customer app then shows no
  // price at all, so staff need to see it here (FR-011).
  pricing?: {
    currency_symbol: string;
    per_day_minor: number | null;
    per_hour_minor: number | null;
  } | null;
  /** 363: the car's reviews, as the customer sees them; null when nobody has reviewed it. */
  rating?: { average: number; count: number } | null;
}

/** 363 (#1090): the Fleet's order. The list keeps newest by default; "Best rated" puts unrated cars last. */
type FleetOrder = 'newest' | 'rating';

interface CarsResponse {
  items: Car[];
  total: number;
  page: number;
  pages: number;
}

/**
 * 050: format a minor-unit amount with the currency symbol the payload carries.
 * Deliberately NOT reusing `formatMinorUnits` from PricingTemplateListPage — that
 * helper is page-private and hardcodes `RM`, which FR-010 forbids.
 */
function rate(minor: number | null | undefined, symbol: string): string | null {
  if (typeof minor !== 'number' || !Number.isFinite(minor)) return null;
  const major = minor / 100;
  return `${symbol} ${Number.isInteger(major) ? major : major.toFixed(2)}`;
}

function RateCell({ pricing }: { pricing: Car['pricing'] }) {
  if (!pricing) {
    return (
      <span className="text-amber-600 text-sm flex items-center gap-1">
        <TriangleAlert size={14} aria-hidden="true" />
        No rate
      </span>
    );
  }
  const daily = rate(pricing.per_day_minor, pricing.currency_symbol);
  const hourly = rate(pricing.per_hour_minor, pricing.currency_symbol);
  return (
    <span className="text-sm">
      {[daily && `${daily}/day`, hourly && `${hourly}/hour`].filter(Boolean).join(' · ')}
    </span>
  );
}

export function CarListPage() {
  const ability = useAbility();
  // H7: the same ability Edit already needs — no new CASL subject or rule.
  const canUpdateCar = ability.can('update', 'Car');
  // 063 (FR-013): gated on `create`, not `update`. A host holds `update Car` but
  // not `create`, so the two are genuinely different answers here — and not on
  // `manage`, which no provider holds at all.
  const canCreateCar = ability.can('create', 'Car');
  // 157 (#8, #9): its own ability, for the same reason `create` is separate from `update` — a
  // host may edit a car without being allowed to remove one from the fleet.
  const canDeleteCar = ability.can('delete', 'Car');

  const [page, setPage] = useState(1);
  const [actionError, setActionError] = useState<string | null>(null);

  /**
   * 158 (#4): find a car by part of its plate.
   *
   * Two pieces of state, not one: what the operator has typed, and what has been asked for. The
   * gap between them is the debounce — the platform's match is an unanchored scan, and a request
   * per keystroke would run six of them to type "SEED00".
   *
   * 🔴 Debounced with a local effect rather than by copying `UserListPage`'s pattern, which parks
   * its timer on `window._emailTimer` — a GLOBAL name. A second page using it would share one
   * timer with the first.
   */
  // 355 (FR-011): the dashboard's "Cars not bookable today" opens this list on `?unbookable_today=true`.
  const [unbookable, clearUnbookable] = useUrlFilter('unbookable_today', ['true'] as const);
  const [plateInput, setPlateInput] = useState('');
  const [plateQuery, setPlateQuery] = useState('');
  const [order, setOrder] = useState<FleetOrder>('newest');

  useEffect(() => {
    const timer = setTimeout(() => {
      setPlateQuery(plateInput.trim());
      // A changed filter always starts at the first page: staying on page 3 of an unfiltered
      // fleet while narrowing to two cars shows an empty page that reads as "no matches".
      setPage(1);
    }, 400);
    return () => clearTimeout(timer);
  }, [plateInput]);

  const carsPath = (() => {
    const params = new URLSearchParams({ page: String(page), limit: '20', sort: order });
    // Only when there is something to filter by — a cleared box must show the whole fleet.
    if (plateQuery) params.set('plate_number', plateQuery);
    if (unbookable) params.set('unbookable_today', 'true');
    return `/cars?${params.toString()}`;
  })();

  const { data, isLoading, error } = useQuery({
    queryKey: [...RESOURCE_KEYS.car.list, page, plateQuery, unbookable, order],
    queryFn: () => api.get<CarsResponse>(carsPath),
    // 063 (FR-010): a refusal is not a transient fault. Retrying it three times
    // only keeps the caller reading the empty state while the truth is on its way.
    retry: false,
  });

  // `invalidateKey` is the page-agnostic prefix of `['admin-cars', page]`, so the badges and
  // the offered action re-render from the server's truth rather than from local state — the
  // whole defect class here is a panel that disagrees with what the backend will serve.
  const publish = useConfirmedMutation({
    mutationFn: (id: string) => api.patch(`/cars/${id}`, PUBLISH_PATCH),
    invalidateKey: RESOURCE_KEYS.car.list,
  });
  const unpublish = useConfirmedMutation({
    mutationFn: (id: string) => api.patch(`/cars/${id}`, UNPUBLISH_PATCH),
    invalidateKey: RESOURCE_KEYS.car.list,
  });
  /**
   * 157 (#8, #9): remove a car from the fleet.
   *
   * A SOFT delete — the platform stamps `deleted_at` and keeps the record, so a car removed by
   * mistake is recoverable and every booking that referenced it still resolves.
   *
   * The platform also refuses, with 409, to delete a car that has confirmed future bookings
   * (`cars/service.ts:322`). That refusal is deliberately NOT restated here: `RowActions` keeps
   * its confirm dialog open on a rejected action and shows the error, so the operator reads the
   * platform's own sentence. A second copy of the rule in the panel could only drift from it.
   */
  const remove = useConfirmedMutation({
    mutationFn: (id: string) => api.delete(`/cars/${id}`),
    invalidateKey: RESOURCE_KEYS.car.list,
  });

  const columns: Column<Car>[] = [
    { key: 'name', header: 'Car' },
    { key: 'plate_number', header: 'Plate' },
    // 377 (#1233, design F): who handles each car.
    {
      key: 'hosts',
      header: 'Hosts',
      render: (row: Car) => <CarHostsChips hosts={row.hosts ?? []} />,
    },
    { key: 'year', header: 'Year' },
    {
      key: 'pricing',
      header: 'Rate',
      render: (row) => <RateCell pricing={row.pricing} />,
    },
    {
      key: 'listing_status',
      header: 'Listing',
      render: (row) => <StatusBadge status={row.listing_status} />,
    },
    {
      key: 'operational_status',
      header: 'Status',
      render: (row) => <StatusBadge status={row.operational_status} />,
    },
    { key: 'seat_capacity', header: 'Seats' },
    {
      key: 'rating',
      header: 'Rating',
      render: (row) => (
        <span
          data-testid="car-rating-cell"
          className="inline-flex items-baseline gap-1 tabular-nums"
        >
          {row.rating ? (
            <>
              <span aria-hidden="true" className="text-semantic-warning-text">
                ★
              </span>
              <span className="font-semibold text-ink">{row.rating.average.toFixed(1)}</span>
              <span className="text-steel">({row.rating.count})</span>
            </>
          ) : (
            <span className="text-steel">No reviews</span>
          )}
        </span>
      ),
    },
    {
      key: 'actions',
      header: 'Actions',
      render: (row) => {
        const verdict = canPublish(row);
        return (
          <RowActions
            actions={[
              {
                key: 'edit',
                label: 'Edit',
                href: `/cars/${row._id}/edit`,
                icon: <Pencil className="h-4 w-4" />,
              },
              // 156 (#11): start a NEW car from this one's specification. A navigation, not an
              // operation — nothing is created until the operator saves the form, so there is
              // nothing to confirm and nothing to undo. Offered only to a caller who may create
              // cars, using the same ability the Add Car button needs.
              canCreateCar && {
                key: 'copy',
                label: 'Copy',
                href: `/cars/new?from=${row._id}`,
                icon: <Copy className="h-4 w-4" />,
              },
              canDeleteCar && {
                key: 'delete',
                label: 'Delete',
                variant: 'danger' as const,
                confirmMessage: `Remove ${row.name} from the fleet? It stops appearing in the panel and the customer app. A car with confirmed future bookings cannot be removed.`,
                onClick: () => remove.run(row._id),
                icon: <Trash2 className="h-4 w-4" />,
              },
              canUpdateCar &&
                (isPublished(row)
                  ? {
                      // Unpublish confirms because it withdraws a live listing, and is offered
                      // even for a car in maintenance — withdrawing is never the unsafe
                      // direction, and refusing it would strand a car that is public and
                      // should not be.
                      key: 'unpublish',
                      label: 'Unpublish',
                      variant: 'danger' as const,
                      confirmMessage: `Withdraw ${row.name} from the customer app? Customers stop seeing it immediately. You can publish it again later.`,
                      onClick: () => unpublish.run(row._id),
                      icon: <Download className="h-4 w-4" />,
                    }
                  : {
                      // Publish does not confirm — it is reversible in one click. Its failure
                      // therefore has no Modal to land in, so the page states it inline; a
                      // silently failed Publish would leave the operator believing a car is
                      // live when it is not.
                      key: 'publish',
                      label: 'Publish',
                      disabled: !verdict.ok,
                      disabledReason: verdict.ok ? undefined : verdict.reason,
                      icon: <Upload className="h-4 w-4" />,
                      onClick: () => {
                        setActionError(null);
                        return publish.run(row._id).catch((e: unknown) => {
                          setActionError(
                            (e as { message?: string })?.message ?? 'Could not publish this car',
                          );
                        });
                      },
                    }),
            ]}
          />
        );
      },
    },
  ];

  return (
    <div>
      <PageHeader
        title="Fleet"
        description="Manage all car listings"
        actions={
          canCreateCar ? (
            <Link to="/cars/new">
              <Button>Add Car</Button>
            </Link>
          ) : undefined
        }
      />
      {unbookable ? (
        <UrlFilterChip
          label="Cars not bookable today"
          onClear={() => {
            clearUnbookable();
            setPage(1);
          }}
        />
      ) : null}
      {/* 158 (#4): the fleet's first filter. Operators reach for a car by its plate — it is what
          is on the key fob and on the vehicle — and this list offered no way to use it. */}
      <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center">
        <Input
          type="search"
          data-testid="plate-filter"
          aria-label="Filter by plate number"
          placeholder="Filter by plate number…"
          className="w-full sm:w-72"
          value={plateInput}
          onChange={(e) => setPlateInput((e.target as HTMLInputElement).value)}
        />
        {/* 363 (design 2h): an Order choice rather than a clickable header — DataTable's headers are plain
            strings shared by every list. */}
        <div className="flex items-center gap-2">
          <label htmlFor="fleet-order" className="text-sm text-steel">
            Order
          </label>
          <Select
            id="fleet-order"
            className="w-40"
            value={order}
            onChange={(e) => {
              setOrder(e.target.value as FleetOrder);
              setPage(1);
            }}
          >
            <option value="newest">Newest</option>
            <option value="rating">Best rated</option>
          </Select>
        </div>
      </div>
      {actionError && (
        <p role="alert" className="mb-4 text-sm text-red-600">
          {actionError}
        </p>
      )}
      <DataTable
        columns={columns}
        data={data?.items ?? []}
        loading={isLoading}
        keyExtractor={(row) => row._id}
        emptyMessage={
          // FR-009: a filter that matched nothing is a different fact from an empty fleet, and an
          // operator who reads the wrong one goes looking for the car in the wrong place.
          plateQuery ? `No cars match “${plateQuery}”.` : 'No cars found'
        }
        // 063: "No cars found" is a statement about the fleet. It must not be
        // made on the strength of a refusal.
        error={error as { status?: number } | null}
        pagination={{
          page: page,
          totalPages: data?.pages ?? 1,
          onPageChange: setPage,
        }}
      />
    </div>
  );
}
